import {UiQlWidgetAssistDisabledReason} from "../model/dto/api/ui-ql-widget-assist-status.dto";
import {UiQlWidgetAssistTurnStatus} from "../model/dto/api/ui-ql-widget-assist-response.dto";
import {UiQlWidgetAssistChangeKind} from "../model/dto/api/ui-ql-widget-assist-change.dto";
import {UiQlAssistStatusBadge} from "../interface/ui-ql-assist-status-badge.interface";

/**
 * Fixed German texts of the chat designer (`UiQlAssistChatComponent`). Everything the LLM writes
 * (explanation, clarification question, assumptions) is rendered as plain text next to these.
 */

/** PII hint (requirement.md L3.7 / capability matrix I-LLM3) - shown verbatim above the input. */
export const UI_QL_ASSIST_PII_HINT =
    'Bitte keine personenbezogenen Daten eingeben – der Assistent sieht nur die Datenstruktur, keine Datensätze.';

/** Headline when the status endpoint answers `enabled = false`. */
export const UI_QL_ASSIST_NOT_CONFIGURED = 'KI-Assistent auf diesem Mandanten nicht konfiguriert';

/**
 * Suggestion chips - the playground presets (`QL_BUILDER_PRESETS`) phrased as requests. Only presets
 * that need no environment-specific id (no generic field / side-car id, no negative case) are used.
 */
export const UI_QL_ASSIST_SUGGESTIONS: ReadonlyArray<string> = [
    'Offene Tickets pro Status als Donut',
    'Tickets pro Monat nach Erstelldatum als Balkendiagramm',
    'Tickets mit ihrem Typ als Tabelle',
    'Kunden mit ihrem bevorzugten Kontakt als Tabelle',
    'Verträge mit Agenturnummer und aktuellem Hauptpartner',
];

/** German reason per `UiQlWidgetAssistStatusDto.reason`. */
export const UI_QL_ASSIST_DISABLED_REASON_TEXTS: Readonly<Record<UiQlWidgetAssistDisabledReason, string>> = {
    modelMissing: 'Für den Assistenten ist kein aktives KI-Modell eingerichtet.',
    noStructuredOutputs: 'Das eingerichtete KI-Modell unterstützt keine strukturierten Ausgaben.',
    budgetExhausted: 'Das tägliche Token-Budget des Mandanten ist aufgebraucht. Bitte später erneut versuchen.',
};

/** Badge per turn status. */
export const UI_QL_ASSIST_STATUS_BADGES: Readonly<Record<UiQlWidgetAssistTurnStatus, UiQlAssistStatusBadge>> = {
    applied: {label: 'Übernommen', severity: 'success'},
    needsClarification: {label: 'Rückfrage', severity: 'info'},
    rejected: {label: 'Abgelehnt', severity: 'danger'},
    unsupported: {label: 'Nicht unterstützt', severity: 'warn'},
};

/** Badge for a status the backend sends but this client does not know yet (newer backend) - never a blank tag. */
export const UI_QL_ASSIST_STATUS_BADGE_UNKNOWN: UiQlAssistStatusBadge = {label: 'Unbekannter Status', severity: 'secondary'};

/** Icon per entry of the list "Änderungen". */
export const UI_QL_ASSIST_CHANGE_ICONS: Readonly<Record<UiQlWidgetAssistChangeKind, string>> = {
    added: 'fa-solid fa-plus text-green-600',
    removed: 'fa-solid fa-minus text-red-600',
    changed: 'fa-solid fa-pen text-blue-600',
};

/** German text of a disabled reason; a reason the client does not know yet gets a neutral text. */
export function uiQlAssistDisabledReasonText(reason: string | null | undefined): string {
    return reason && Object.prototype.hasOwnProperty.call(UI_QL_ASSIST_DISABLED_REASON_TEXTS, reason)
        ? UI_QL_ASSIST_DISABLED_REASON_TEXTS[reason as UiQlWidgetAssistDisabledReason]
        : 'Der Assistent ist auf diesem Mandanten nicht freigeschaltet.';
}
